/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { MaintenancePlanDetailCell } from '@euler/app/flows/plans/pages/list/components/MaintenancePlanCell';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { MaintenancePlanBottomSheet } from '@euler/app/flows/task/pages/finish-task/components/MaintenancePlanBottomSheet';
import {
  MaintenancePlanListItem,
  MaintenancePlanListItemType,
} from '@euler/app/flows/task/pages/finish-task/types';
import { maintenancePlanListItemFromPlanDetail } from '@euler/app/flows/task/pages/finish-task/utils';
import { EmptyView } from '@euler/components/EmptyView';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import {
  kDefaultMaxElapseTimeInDaysBeforeCanUpdateMaintenancePlans,
  kDefaultRolesAllowedToFinishOrder,
} from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useFetchAsyncState } from '@euler/functions/useFetchAsyncState';
import {
  MaintenancePlan,
  MaintenancePlanDetail,
} from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { makeUniqueId } from '@euler/utils/id';
import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import { memo, useCallback, useState } from 'react';
import Toast from 'react-native-root-toast';

export const MaintenancePlans = memo(() => {
  const loading = useAppLoading();

  const { serviceRecord, storeConfiguration } = useServiceRecordContext();
  const { user } = useCurrentUserDangerously();

  const roleHelper = useCreation(
    () => new OrgUserRoleHelper(user.role),
    [user.role],
  );

  const updatableRoles = storeConfiguration
    .restrictedRolesForUpdateMaintenancePlans?.length
    ? storeConfiguration.restrictedRolesForUpdateMaintenancePlans
    : storeConfiguration.restrictedRolesForFinishOrder?.length
    ? storeConfiguration.restrictedRolesForFinishOrder
    : kDefaultRolesAllowedToFinishOrder;

  const canUpdateMaintenancePlans = roleHelper.hasAnyRole(...updatableRoles);

  const { maintenancePlanService } = useServiceFactory();
  const { rpx } = useResponsiveContext();

  const [activeItem, setActiveItem] = useState<MaintenancePlanListItem>();

  const [state, refresh, setState] = useFetchAsyncState(
    async () => {
      const res = await maintenancePlanService.search(
        {
          orderNo: serviceRecord.orderNo,
        },
        0,
        0,
      );
      return res;
    },
    undefined,
    true,
  );

  const onAddCustomMaintenancePlanItem = useCallback(() => {
    setActiveItem({ id: makeUniqueId(), type: 'custom', name: '' });
  }, []);

  const onCancelEditPlanInfo = useCallback(() => {
    setActiveItem(undefined);
  }, []);

  const onDoneEditPlanInfo = usePersistFn(
    async (values: {
      id: string;
      name: string;
      type: MaintenancePlanListItemType;
      nextServiceDate: string | Date;
      nextServiceMileage: number | undefined;
      remark?: string;
    }) => {
      await loading.showWithAction(async () => {
        if (activeItem == null) return;

        if (activeItem.existing != null) {
          const updated =
            await maintenancePlanService.updateMaintenancePlanDetail(
              activeItem.existing.plan.planNo,
              activeItem.existing.detail.id,
              {
                name: values.name,
                nextServiceDate: values.nextServiceDate,
                nextServiceMileage: values.nextServiceMileage,
                remark: values.remark,
              },
            );
          onDetailUpdated(updated);
          Toast.show('计划修改成功!', {
            duration: 1000,
            position: Toast.positions.BOTTOM,
          });
        } else {
          await maintenancePlanService.appendCustomMaintenancePlan({
            orderNo: serviceRecord.orderNo,
            name: values.name,
            remark: values.remark,
            nextServiceDate: values.nextServiceDate,
            nextServiceMileage: values.nextServiceMileage,
          });
          void refresh();
          Toast.show('计划添加成功!', {
            duration: 1000,
            position: Toast.positions.BOTTOM,
          });
        }
        setActiveItem(undefined);
      });
    },
  );

  const onDetailUpdated = usePersistFn((detail: MaintenancePlanDetail) => {
    const updatedState = { ...state };
    if (updatedState.status !== 'success' || updatedState.result?.[0] == null) {
      return;
    }
    const plan = updatedState.result[0];
    const index = plan.details.findIndex(d => d.id === detail.id);
    if (index < 0) return;

    const details = [...plan.details];
    details[index] = detail;

    const updatedPlan = { ...plan, details };
    updatedState.result = updatedState.result.map(p =>
      p.id === plan.id ? updatedPlan : p,
    );
    setState(updatedState);
  });

  const onDetailRemoved = usePersistFn((detail: MaintenancePlanDetail) => {
    const updatedState = { ...state };
    if (updatedState.status !== 'success' || updatedState.result?.[0] == null) {
      return;
    }
    const plan = updatedState.result[0];
    const details = plan.details.filter(d => d.id !== detail.id);
    const updatedPlan = { ...plan, details };
    updatedState.result = updatedState.result.map(p =>
      p.id === plan.id ? updatedPlan : p,
    );
    setState(updatedState);
  });

  const onEditPlanDetail = useCallback(
    (plan: MaintenancePlan, detail: MaintenancePlanDetail) => {
      const item = maintenancePlanListItemFromPlanDetail(plan, detail);
      setActiveItem(item);
    },
    [],
  );

  if (state.status !== 'success' || state.result == null) {
    return null;
  }

  const plan = state.result[0] as MaintenancePlan | undefined;

  const editable =
    canUpdateMaintenancePlans &&
    isBefore(
      new Date(),
      addDays(
        dateFromValue(plan?.createdAt ?? serviceRecord.createdAt),
        storeConfiguration.maxElapseTimeInDaysBeforeCanUpdateMaintenancePlans ??
          kDefaultMaxElapseTimeInDaysBeforeCanUpdateMaintenancePlans,
      ),
    );

  if (!editable && (!state.result?.length || !state.result[0].details.length)) {
    return null;
  }

  return (
    <>
      <TaskDetailSection
        title={'保养计划'}
        headerRight={
          editable ? (
            <LinkButton
              text="新增自定义保养计划"
              icon="plus"
              textSize={rpx(12)}
              onPress={onAddCustomMaintenancePlanItem}
            />
          ) : null
        }
      >
        {plan != null ? (
          <StackLayout gap={rpx(1)} alignItems="stretch">
            {plan.details.map(detail => (
              <MaintenancePlanDetailCell
                key={detail.id}
                plan={plan}
                detail={detail}
                editable={editable}
                nameContainerStyle={{
                  backgroundColor: '#eee',
                }}
                nameStyle={{
                  color: '#333',
                  fontFamily: FontFamily.NotoSans.Light,
                }}
                onEdit={onEditPlanDetail}
                onRemoved={onDetailRemoved}
                onUpdated={onDetailUpdated}
              />
            ))}
          </StackLayout>
        ) : (
          <EmptyView
            noFullHeight
            message="未添加相关保养计划"
            style={{ paddingBottom: rpx(20) }}
            messageStyle={{
              marginTop: rpx(5),
              fontSize: rpx(11),
              fontFamily: FontFamily.NotoSans.Medium,
            }}
            iconColor="#ccc"
            iconSize={rpx(32)}
          />
        )}
      </TaskDetailSection>
      <MaintenancePlanBottomSheet
        show={activeItem != null}
        target={activeItem}
        defaultNextServiceDate={activeItem?.planInfo?.nextServiceDate}
        defaultNextServiceMileage={activeItem?.planInfo?.nextServiceMileage}
        onClose={onCancelEditPlanInfo}
        onDone={onDoneEditPlanInfo}
      />
    </>
  );
});
